import { useState, useEffect, useMemo, useCallback } from "react";
import { ReportsService } from "../services/reports.service";
import { SchoolService } from "../services/school.service";
import { ExamType, SchoolWithExamRegistration } from "../types/types";

interface UseSchoolExamReportsReturn {
  examTypes: ExamType[];
  selectedExamTypeIds: number[];
  selectedExamTypes: ExamType[];
  registrationStatus: "paid" | "unpaid" | "incomplete" | "all";
  paymentStatus: "paid" | "unpaid" | "all";
  registrationData: SchoolWithExamRegistration | null;
  error: string | null;
  loading: boolean;
  isALSelected: boolean;
  setRegistrationStatus: (
    status: "paid" | "unpaid" | "incomplete" | "all"
  ) => void;
  setPaymentStatus: (status: "paid" | "unpaid" | "all") => void;
  handleExamTypeSelection: (examType: ExamType) => void;
  schoolId: number | null;
}

export const useSchoolExamReports = (): UseSchoolExamReportsReturn => {
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [selectedExamTypeIds, setSelectedExamTypeIds] = useState<number[]>([]);
  const [registrationStatus, setRegistrationStatus] = useState<
    "paid" | "unpaid" | "incomplete" | "all"
  >("all");
  const [paymentStatus, setPaymentStatus] = useState<"paid" | "unpaid" | "all">(
    "all"
  );
  const [registrationData, setRegistrationData] =
    useState<SchoolWithExamRegistration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [schoolId, setSchoolId] = useState<number | null>(null);

  // Derive selectedExamTypes from selectedExamTypeIds and examTypes
  const selectedExamTypes = useMemo(() => {
    return examTypes.filter((examType) =>
      selectedExamTypeIds.includes(examType.id)
    );
  }, [examTypes, selectedExamTypeIds]);

  // Determine if any selected exam type contains "A/L" (GCE or TVE)
  const isALSelected = useMemo(() => {
    return selectedExamTypes.some(
      (examType) =>
        examType.name.includes("GCE A/L") || examType.name.includes("TVE A/L")
    );
  }, [selectedExamTypes]);

  // Fetch user's school and exam types on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch user's school
        const schoolResponse = await SchoolService.getOwnSchool();
        setSchoolId(schoolResponse.id);

        // Fetch exam types registered by the school
        if (schoolResponse.id) {
          const examTypesResponse = await ReportsService.getSchoolExamTypes(
            schoolResponse.id
          );
          setExamTypes(examTypesResponse.data || []);
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError("Failed to fetch school and exam types");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch school report when exam types are selected
  useEffect(() => {
    if (selectedExamTypeIds.length > 0 && schoolId) {
      fetchSchoolReport();
    } else {
      setRegistrationData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamTypeIds, registrationStatus, paymentStatus, schoolId]);

  const fetchSchoolReport = async () => {
    if (selectedExamTypeIds.length === 0 || !schoolId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await ReportsService.getSchoolExamReports({
        exam_type_ids: selectedExamTypeIds,
        school_id: schoolId,
        registration_status: registrationStatus,
        payment_status: paymentStatus,
      });

      setRegistrationData(response as SchoolWithExamRegistration);
    } catch (err) {
      console.error("Error fetching school report:", err);
      setError("Failed to fetch school report");
    } finally {
      setLoading(false);
    }
  };

  const handleExamTypeSelection = useCallback(
    (examType: ExamType) => {
      const isSelected = selectedExamTypeIds.includes(examType.id);
      let newSelectedIds = [...selectedExamTypeIds];
      const groupKeywords = ["BEPC", "PROB", "BACC", "CAP", "TTT", "DIS"];

      if (isSelected) {
        // Remove the exam type
        newSelectedIds = newSelectedIds.filter((id) => id !== examType.id);

        // Auto-deselect related exam types
        // First, for groups
        for (const keyword of groupKeywords) {
          if (examType.name.includes(keyword)) {
            const groupExamTypes = examTypes.filter((et) =>
              et.name.includes(keyword)
            );
            newSelectedIds = newSelectedIds.filter(
              (id) => !groupExamTypes.some((et) => et.id === id)
            );
            break;
          }
        }
      } else {
        // Add the exam type
        newSelectedIds.push(examType.id);

        // Auto-select related exam types
        // First, for groups
        for (const keyword of groupKeywords) {
          if (examType.name.includes(keyword)) {
            const groupExamTypes = examTypes.filter(
              (et) =>
                et.name.includes(keyword) && !newSelectedIds.includes(et.id)
            );
            newSelectedIds.push(...groupExamTypes.map((et) => et.id));
            break;
          }
        }
      }

      setSelectedExamTypeIds(newSelectedIds);
    },
    [selectedExamTypeIds, examTypes]
  );

  return {
    examTypes,
    selectedExamTypeIds,
    selectedExamTypes,
    registrationStatus,
    paymentStatus,
    registrationData,
    error,
    loading,
    isALSelected,
    setRegistrationStatus,
    setPaymentStatus,
    handleExamTypeSelection,
    schoolId,
  };
};
